import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from '@tanstack/react-table';
import { useVirtual } from 'react-virtual';

// ASSETS
import UserIcon from 'assets/AgreementIcons/User';
import EditIcon from 'assets/EditIcon';

// COMPONENTS
import { SecondaryButton } from 'components/Buttons';
import { SimpleLoading } from 'components/Loading';
import UserManagementModals from '../components';
import FlashMessage from 'components/FlashMessage';
import Ellipsing from 'components/Ellipsing/Ellipsing.component';

// CONSTANTS
import { USER_ROLES, USER_ROLES_TRANSLATION, FLASH_MESSAGE_TYPES, INITIAL_FLASH_MESSAGE } from 'constants/common';

// HOOKS
import { usePrevious } from 'hooks';
import { useAppDispatch } from 'hooks/store';

// SELECTORS
import { selectUsers, selectLoading, selectErrors } from 'services/Users/selectors';
import { selectUser } from 'services/Authentication/selectors';

// STYLES
import colors from 'styles/colors';
import { UserCount, AddAccountRow } from './accounts.styles';
import { OutsideLink } from '../../FleetSettings.styles';
import {
  LoadingState,
  SortableHeader,
  SortingIcon,
  StyledActionButtonsContainer,
  StyledSortableIcon,
  TableContainer,
} from 'components/SectionTable';
import { Section, SectionHeader, SectionContent, SectionHeaderTitle } from 'styles/cards';

// THUNKS
import { getUsers, resendActivation } from 'services/Users/thunks';

// TYPES
import { AuthUser } from 'services/resources';

const ResendActivationButton = ({ userId }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [activationLoading, setActivationLoading] = useState(false);
  const errors = useSelector(selectErrors());
  const loading = useSelector(selectLoading());

  const prevLoading = usePrevious(loading);

  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  useEffect(() => {
    if (loading === false && prevLoading === true && activationLoading) {
      setActivationLoading(false);
      if (Object.keys(errors).length) {
        // @ts-expect-error TS(2345): Argument of type '{ shown: true; type: number; tra... Remove this comment to see the full error message
        setFlashMessageState({
          shown: true,
          type: FLASH_MESSAGE_TYPES.error,
          translationId: 'views.FleetSettings.UserManagement.Accounts.activationError',
        });
      } else {
        // @ts-expect-error TS(2345): Argument of type '{ shown: true; type: number; tra... Remove this comment to see the full error message
        setFlashMessageState({
          shown: true,
          type: FLASH_MESSAGE_TYPES.success,
          translationId: 'views.FleetSettings.UserManagement.Accounts.activationSent',
        });
      }
    }
  }, [activationLoading, errors, loading, prevLoading]);

  return (
    <>
      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={intl.formatMessage({ id: flashMessageState.translationId })}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      <SecondaryButton
        onClick={() => {
          setActivationLoading(true);
          dispatch(resendActivation(userId));
        }}
        loading={activationLoading}
      >
        {intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.resendActivation',
        })}
      </SecondaryButton>
    </>
  );
};

const Accounts = () => {
  const intl = useIntl();
  const users = useSelector(selectUsers());
  const dispatch = useAppDispatch();
  const currentUser = useSelector(selectUser());
  const [loadingUsers, setLoadingUsers] = useState(false);
  const columnHelper = createColumnHelper<AuthUser>();

  useEffect(() => {
    const fetchUsersForTable = async () => {
      setLoadingUsers(true);
      await dispatch(getUsers());
      setLoadingUsers(false);
    };
    fetchUsersForTable();
  }, [dispatch]);

  const [sorting, setSorting] = useState([]);

  const data = useMemo(() => users, [users]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        size: 220,
        header: intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.headers.email',
        }),
        cell: (email) => <Ellipsing text={email.getValue()} />,
      }),
      columnHelper.accessor('firstName', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.headers.firstName',
        }),
        size: 100,
        cell: (firstName) => <Ellipsing text={firstName.getValue()} />,
      }),
      columnHelper.accessor('lastName', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.headers.lastName',
        }),
        size: 100,
        cell: (lastName) => <Ellipsing text={lastName.getValue()} />,
      }),
      columnHelper.accessor('profile.role', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.headers.permissions',
        }),
        cell: (userRole) => {
          const permissionName = intl.formatMessage({
            id: USER_ROLES_TRANSLATION.find((role) => role.id === userRole.getValue())?.translationId,
          });

          return <Ellipsing text={permissionName} />;
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => null,
        size: 160,
        enableResizing: false,
        cell: ({ row }) => {
          const user = row.original;

          return user ? (
            <StyledActionButtonsContainer>
              {!user.isActive && <ResendActivationButton userId={user.id} />}
              <UserManagementModals.EditUser user={user}>
                <EditIcon color={colors.fleetBlue} />
              </UserManagementModals.EditUser>
            </StyledActionButtonsContainer>
          ) : null;
        },
      }),
    ],
    [columnHelper, intl]
  );

  const columnVisibility = {
    actions: currentUser.profile.role === USER_ROLES.admin,
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const tableContainerRef = useRef(null);

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 4,
  });
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const showSortingIcons = (canSort, isSorted) => {
    if (canSort && !isSorted) {
      return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
    }
    if (isSorted) {
      return (
        <SortingIcon
          size={14}
          color={colors.fleetDarkGray}
          direction={
            {
              asc: SortingIcon.Direction.Up,
              desc: SortingIcon.Direction.Down,
            }[isSorted]
          }
        />
      );
    }
    return null;
  };

  return (
    <Section>
      <SectionHeader>
        <SectionHeaderTitle>
          <UserIcon color={colors.blue} />
          {intl.formatMessage({
            id: 'views.FleetSettings.UserManagement.Accounts.title',
          })}
        </SectionHeaderTitle>
      </SectionHeader>
      <SectionContent>
        <TableContainer ref={tableContainerRef}>
          {!loadingUsers || rows.length > 0 ? (
            <table className="table">
              <thead className="thead">
                {
                  // Loop over the header rows
                  table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isSorted = header.column.getIsSorted();
                        const canSort = header.column.getCanSort();
                        return (
                          <SortableHeader
                            key={header.id}
                            $sortable={canSort}
                            onClick={header.column.getToggleSortingHandler()}
                            style={{
                              width: header.getSize(),
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div>
                                {header.isPlaceholder || header.id === 'actions' ? null : (
                                  <div>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {showSortingIcons(canSort, isSorted)}
                                  </div>
                                )}
                              </div>
                            )}
                          </SortableHeader>
                        );
                      })}
                    </tr>
                  ))
                }
              </thead>
              <tbody className="tbody">
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px` }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <LoadingState>
              <SimpleLoading color={colors.fleetBlue} />
            </LoadingState>
          )}
        </TableContainer>
        <UserCount>
          {intl.formatMessage(
            { id: 'views.FleetSettings.UserManagement.Accounts.userNumber' },
            {
              number: table.getRowModel().rows.length,
              isLoading: loadingUsers,
            }
          )}
        </UserCount>
        {currentUser.profile.role === USER_ROLES.admin && (
          <AddAccountRow>
            <UserManagementModals.AddUser />
            <OutsideLink
              target="_blank"
              rel="noopener"
              href={intl.formatMessage({
                id: 'views.FleetSettings.UserManagement.Accounts.learnMoreLink',
              })}
            >
              {intl.formatMessage({
                id: 'views.FleetSettings.UserManagement.Accounts.learnMore',
              })}
            </OutsideLink>
          </AddAccountRow>
        )}
      </SectionContent>
    </Section>
  );
};

export default Accounts;
